/**
 * ImportUsersWizard — Multi-step wizard for bulk user import.
 *
 * Steps:
 *  1. Upload — Select a file (xlsx, csv, json, etc.)
 *  2. Parsing — AI parses the file (PubSub progress)
 *  3. Preview — Review & edit parsed rows
 *  4. Confirm — Execute invitations with progress
 *  5. Results — Summary of created/failed users
 */

import React, { useState, useCallback, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import {
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiXLight,
  PiPencilLight,
  PiTrashLight,
  PiDownloadLight,
  PiUsersLight,
} from 'react-icons/pi';

import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import { useImportPubSub } from '../../hooks/useImportPubSub';
import {
  uploadUsersImport,
  getImportStatus,
  updateImportPreview,
  confirmImport,
} from '../../services/import/importApi';
import type {
  ImportWizardStep,
  ImportUserRow,
  ImportPreviewData,
  ImportConfirmResponse,
  ImportJobStatus,
} from '../../types/import';

interface ImportUsersWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS: { key: ImportWizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload File' },
  { key: 'parsing', label: 'AI Parsing' },
  { key: 'preview', label: 'Preview & Edit' },
  { key: 'confirm', label: 'Importing' },
  { key: 'results', label: 'Results' },
];

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

export default function ImportUsersWizard({ isOpen, onClose, onComplete }: ImportUsersWizardProps) {
  const { t } = useTranslation();

  // Wizard state
  const [step, setStep] = useState<ImportWizardStep>('upload');
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(null);
  const [confirmResult, setConfirmResult] = useState<ImportConfirmResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);

  // PubSub hook for AI parsing progress
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: step === 'parsing',
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadPreview(uuid || jobUuid!);
        setStep('preview');
      }
    },
    onFailed: (err) => {
      setError(err);
      setStep('upload');
    },
  });

  // Fetch available roles on open
  useEffect(() => {
    if (!isOpen) return;
    const fetchRoles = async () => {
      try {
        const response = await apis.get('/admin/roles');
        if (response.data.success && response.data.data) {
          const opts: DropdownOption[] = response.data.data.map((role: any) => ({
            value: role.name,
            label: formatRoleNameForDisplay(role.name),
          }));
          setRoleOptions(opts);
        }
      } catch {
        // Fallback defaults if roles API fails
        setRoleOptions([
          { value: 'full_access', label: 'Full Access' },
          { value: 'edit_access', label: 'Edit Access' },
          { value: 'read_access', label: 'Read Access' },
        ]);
      }
    };
    fetchRoles();
  }, [isOpen]);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setJobUuid(null);
      setCorrelationId(null);
      setSelectedFile(null);
      setPreviewData(null);
      setConfirmResult(null);
      setIsLoading(false);
      setError(null);
      setEditingRow(null);
      resetProgress();
    }
  }, [isOpen, resetProgress]);

  // ── Step 1: File Upload ──
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await uploadUsersImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setStep('parsing');
      } else {
        const e = response.error;
        setError(typeof e === 'string' ? e : typeof e === 'object' && e ? (e.description || e.title || 'Upload failed') : 'Upload failed');
      }
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.description || errorData?.title || err.message || 'Upload failed';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile]);

  // ── Step 2: Load preview from API ──
  const loadPreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        setPreviewData(response.preview);
        // Select all rows by default and set default role
        const rows = response.preview.rows as ImportUserRow[];
        rows.forEach((row) => {
          const hasDomainMismatch = row.domain_match === false || (row.warnings || []).includes('email_domain_mismatch');
          row._selected = !row.is_duplicate && !hasDomainMismatch;
          if (!row.role) {
            row.role = 'full_access';
          }
        });
        setPreviewData({ ...response.preview, rows });
      }
    } catch (err: any) {
      setError('Failed to load preview');
    }
  }, []);

  // ── Step 3: Preview editing ──
  const toggleRow = useCallback((idx: number) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows[idx] = { ...rows[idx], _selected: !rows[idx]._selected };
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const toggleAll = useCallback((selected: boolean) => {
    if (!previewData) return;
    const rows = (previewData.rows as ImportUserRow[]).map((r) => {
      const hasDomainMismatch = r.domain_match === false || (r.warnings || []).includes('email_domain_mismatch');
      return {
        ...r,
        _selected: (r.is_duplicate || hasDomainMismatch) ? false : selected,
      };
    });
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const updateRow = useCallback((idx: number, updates: Partial<ImportUserRow>) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows[idx] = { ...rows[idx], ...updates, _edited: true };
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  const removeRow = useCallback((idx: number) => {
    if (!previewData) return;
    const rows = [...previewData.rows] as ImportUserRow[];
    rows.splice(idx, 1);
    setPreviewData({ ...previewData, rows });
  }, [previewData]);

  // ── Step 4: Confirm ──
  const handleConfirm = useCallback(async () => {
    if (!jobUuid || !previewData) return;

    const selectedRows = (previewData.rows as ImportUserRow[]).filter((r) => r._selected);
    if (selectedRows.length === 0) {
      setError('No rows selected for import');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save the current preview (with selections/edits) first
      await updateImportPreview(jobUuid, selectedRows, {
        total_rows: selectedRows.length,
      });

      // Execute the import
      setStep('confirm');
      const result = await confirmImport(jobUuid);
      setConfirmResult(result);
      setStep('results');
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.description || errorData?.title || err.message || 'Import failed';
      setError(errorMsg);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, previewData]);

  const handleClose = useCallback(() => {
    if (confirmResult && confirmResult.created_count > 0) {
      onComplete?.();
    }
    onClose();
  }, [confirmResult, onComplete, onClose]);

  // ── Render ──
  const currentStepIndex = STEPS.findIndex((s) => s.key === step);
  const rows = previewData?.rows as ImportUserRow[] | undefined;
  const selectedCount = rows?.filter((r) => r._selected).length || 0;
  const duplicateCount = rows?.filter((r) => r.is_duplicate).length || 0;
  const domainMismatchCount = rows?.filter((r) => r.domain_match === false || (r.warnings || []).includes('email_domain_mismatch')).length || 0;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <PiUsersLight className="w-6 h-6 text-blue-600" />
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('Import Users')}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <PiXLight className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Step indicator */}
                <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    {STEPS.map((s, i) => (
                      <div key={s.key} className="flex items-center">
                        <div
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            i === currentStepIndex
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              : i < currentStepIndex
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                          }`}
                        >
                          {i < currentStepIndex ? (
                            <PiCheckCircleLight className="w-3.5 h-3.5" />
                          ) : null}
                          {t(s.label)}
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className="w-6 h-px bg-gray-200 dark:bg-gray-700 mx-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6 min-h-[400px] max-h-[60vh] overflow-y-auto">
                  {/* Error banner */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                      <PiWarningCircleLight className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                      <button onClick={() => setError(null)} className="ml-auto">
                        <PiXLight className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Step: Upload */}
                  {step === 'upload' && (
                    <div className="flex flex-col items-center gap-6">
                      <div className="text-center">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          {t('Upload a file with user data')}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {t('Supported formats: Excel (.xlsx), CSV, JSON, PDF, DOCX, TXT')}
                        </p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                          {t('AI will automatically parse and normalize the data regardless of format.')}
                        </p>
                      </div>

                      {/* FileUploader for user data file */}
                      <div className="w-full max-w-md">
                        <FileUploader
                          files={selectedFile ? [selectedFile] : []}
                          onFilesChange={(files) => {
                            setSelectedFile(files[0] || null);
                            setError(null);
                          }}
                          allowedExtensions={ALLOWED_EXTENSIONS}
                          maxFiles={1}
                        />
                      </div>

                      <Button
                        variant="primary"
                        size="small"
                        onClick={handleUpload}
                        disabled={!selectedFile || isLoading}
                        isLoading={isLoading}
                      >
                        {t('Upload & Parse')}
                      </Button>
                    </div>
                  )}

                  {/* Step: Parsing */}
                  {step === 'parsing' && (
                    <div className="flex flex-col items-center gap-4 py-12">
                      <PiSpinnerGapLight className="w-12 h-12 text-blue-500 animate-spin" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        {t('Analysing your file...')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {progress.message || t('This usually takes 10-30 seconds')}
                      </p>
                    </div>
                  )}

                  {/* Step: Preview */}
                  {step === 'preview' && rows && (
                    <div className="space-y-4">
                      {/* Summary bar */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-700 dark:text-gray-300">
                            {t('Total')}: <strong>{rows.length}</strong>
                          </span>
                          <span className="text-green-600">
                            {t('Selected')}: <strong>{selectedCount}</strong>
                          </span>
                          {duplicateCount > 0 && (
                            <span className="text-amber-600">
                              {t('Duplicates')}: <strong>{duplicateCount}</strong>
                            </span>
                          )}
                          {domainMismatchCount > 0 && (
                            <span className="text-red-600">
                              {t('Domain Mismatch')}: <strong>{domainMismatchCount}</strong>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleAll(true)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {t('Select All')}
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => toggleAll(false)}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            {t('Deselect All')}
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-3 py-2 text-left w-10">
                                <input
                                  type="checkbox"
                                  checked={selectedCount === rows.filter((r) => !r.is_duplicate).length}
                                  onChange={(e) => toggleAll(e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('First Name')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Last Name')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Email')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Role')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">
                                {t('Status')}
                              </th>
                              <th className="px-3 py-2 w-16" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {rows.map((row, idx) => (
                              <tr
                                key={idx}
                                className={`${
                                  row.is_duplicate
                                    ? 'bg-amber-50/50 dark:bg-amber-900/10'
                                    : (row.domain_match === false || (row.warnings || []).includes('email_domain_mismatch'))
                                    ? 'bg-red-50/50 dark:bg-red-900/10'
                                    : row._selected
                                    ? 'bg-white dark:bg-gray-900'
                                    : 'bg-gray-50/50 dark:bg-gray-800/50 opacity-60'
                                } hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors`}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={!!row._selected}
                                    disabled={!!row.is_duplicate || row.domain_match === false || (row.warnings || []).includes('email_domain_mismatch')}
                                    onChange={() => toggleRow(idx)}
                                    className="rounded border-gray-300"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  {editingRow === idx ? (
                                    <Input
                                      type="text"
                                      value={row.first_name}
                                      onChange={(e) => updateRow(idx, { first_name: e.target.value })}
                                    />
                                  ) : (
                                    <span className="text-gray-900 dark:text-gray-100">
                                      {row.first_name}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {editingRow === idx ? (
                                    <Input
                                      type="text"
                                      value={row.last_name}
                                      onChange={(e) => updateRow(idx, { last_name: e.target.value })}
                                    />
                                  ) : (
                                    <span className="text-gray-900 dark:text-gray-100">
                                      {row.last_name}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {editingRow === idx ? (
                                    <Input
                                      type="email"
                                      value={row.email}
                                      onChange={(e) => updateRow(idx, { email: e.target.value })}
                                    />
                                  ) : (
                                    <span className="text-gray-900 dark:text-gray-100">
                                      {row.email}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {editingRow === idx ? (
                                    <Dropdown
                                      options={roleOptions}
                                      placeholder={t('Select role')}
                                      value={row.role}
                                      onChange={(val) => {
                                        const v = Array.isArray(val) ? val[0] : val;
                                        updateRow(idx, { role: v });
                                      }}
                                      compact
                                    />
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                      {row.role ? formatRoleNameForDisplay(row.role) : '—'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {row.is_duplicate ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                      {t('Duplicate')}
                                    </span>
                                  ) : (row.domain_match === false || (row.warnings || []).includes('email_domain_mismatch')) ? (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                      title={row.warning || t('Email domain does not match your organization')}
                                    >
                                      {t('Domain Mismatch')}
                                    </span>
                                  ) : row.warning ? (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                                      title={row.warning}
                                    >
                                      {t('Warning')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                      {t('Ready')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() =>
                                        setEditingRow(editingRow === idx ? null : idx)
                                      }
                                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600"
                                      title={t('Edit')}
                                    >
                                      <PiPencilLight className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => removeRow(idx)}
                                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500"
                                      title={t('Remove')}
                                    >
                                      <PiTrashLight className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Step: Confirm (processing) */}
                  {step === 'confirm' && (
                    <div className="flex flex-col items-center gap-4 py-12">
                      <PiSpinnerGapLight className="w-12 h-12 text-blue-500 animate-spin" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        {t('Creating user accounts & sending invitations...')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('This may take a moment. Please do not close this window.')}
                      </p>
                    </div>
                  )}

                  {/* Step: Results */}
                  {step === 'results' && confirmResult && (
                    <div className="space-y-6">
                      {/* Summary card */}
                      <div className="flex items-center gap-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
                        {confirmResult.created_count > 0 ? (
                          <PiCheckCircleLight className="w-10 h-10 text-green-500" />
                        ) : (
                          <PiWarningCircleLight className="w-10 h-10 text-red-500" />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {confirmResult.created_count > 0
                              ? t('Import Completed')
                              : t('Import Failed')}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('{{created}} of {{total}} users imported successfully', {
                              created: confirmResult.created_count,
                              total: confirmResult.created_count + confirmResult.failed_count,
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className={`grid gap-4 ${confirmResult.skipped_existing ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <div className="p-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                          <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                            {confirmResult.created_count}
                          </div>
                          <div className="text-sm text-green-600 dark:text-green-400">
                            {t('Users Invited')}
                          </div>
                        </div>
                        {!!confirmResult.skipped_existing && confirmResult.skipped_existing > 0 && (
                          <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                            <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                              {confirmResult.skipped_existing}
                            </div>
                            <div className="text-sm text-amber-600 dark:text-amber-400">
                              {t('Already Existed')}
                            </div>
                          </div>
                        )}
                        <div className="p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                          <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                            {confirmResult.failed_count}
                          </div>
                          <div className="text-sm text-red-600 dark:text-red-400">
                            {t('Failed')}
                          </div>
                        </div>
                      </div>

                      {/* Failed items */}
                      {confirmResult.failed_items.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('Failed Items')}
                          </h4>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {confirmResult.failed_items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/10 rounded-lg"
                              >
                                <PiWarningCircleLight className="w-4 h-4 text-red-500 flex-shrink-0" />
                                <span className="text-gray-600 dark:text-gray-400">
                                  Row {item.row + 1}
                                  {item.email && ` (${item.email})`}: {item.error}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div>
                    {step === 'preview' && (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-gray-500">
                          {selectedCount} {t('users will be invited')}
                        </span>
                        {domainMismatchCount > 0 && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {t('{{count}} users excluded — email domain does not match your organization', { count: domainMismatchCount })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {(step === 'upload' || step === 'results') && (
                      <Button variant="secondary" size="small" onClick={handleClose}>
                        {step === 'results' ? t('Done') : t('Cancel')}
                      </Button>
                    )}
                    {step === 'preview' && (
                      <>
                        <Button variant="secondary" size="small" onClick={() => { setStep('upload'); setPreviewData(null); }}>
                          {t('Back')}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleConfirm}
                          disabled={selectedCount === 0 || isLoading}
                          isLoading={isLoading}
                        >
                          {t('Confirm & Import {{count}} Users', { count: selectedCount })}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
